import { useEffect, useMemo, useRef, useState } from 'react';
import type { ScanImage, ScanImagesResponse } from '../types';
import { fetchScanImages } from '../hooks/useJobs';
import { createLogger } from '../utils/logger';

const log = createLogger('ImagesModal');

interface ImagesModalProps {
  isOpen: boolean;
  jobId: string | null;
  scanId: string | null;
  onClose: () => void;
}

function inferExtension(url: string): string {
  try {
    const path = new URL(url).pathname;
    const match = path.match(/\.([a-zA-Z0-9]{1,5})$/);
    if (match) return match[1].toLowerCase();
  } catch {
    // ignore parse errors and fall back below
  }
  return 'jpg';
}

function buildFilename(image: ScanImage, scanId: string | null): string {
  const ext = inferExtension(image.url);
  const parts = [scanId ?? 'image', image.label];
  if (image.camera) parts.push(image.camera);
  return `${parts.join('_')}.${ext}`;
}

async function downloadImage(image: ScanImage, scanId: string | null): Promise<void> {
  const filename = buildFilename(image, scanId);
  try {
    const response = await fetch(image.url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    log.debug(`Downloaded image ${filename}`);
  } catch (err) {
    log.error(`Failed to download image ${filename}:`, err);
    window.open(image.url, '_blank', 'noopener,noreferrer');
  }
}

interface ImageCardProps {
  image: ScanImage;
  onOpen: (image: ScanImage) => void;
  onDownload: (image: ScanImage) => void;
}

function ImageCard({ image, onOpen, onDownload }: ImageCardProps) {
  const [errored, setErrored] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(image);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(image)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(image);
        }
      }}
      className="group block bg-surface-50 rounded-lg overflow-hidden border border-surface-200 hover:border-primary-400 hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400"
      title="Click to enlarge"
    >
      <div className="aspect-video bg-surface-100 flex items-center justify-center overflow-hidden">
        {errored ? (
          <div className="text-surface-400 text-xs flex flex-col items-center gap-1 p-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Failed to load</span>
          </div>
        ) : (
          <img
            src={image.url}
            alt={image.label}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={() => setErrored(true)}
          />
        )}
      </div>
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-surface-700 capitalize truncate">
          {image.label.replace(/_/g, ' ')}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {image.camera && (
            <span className="font-mono text-[10px] text-surface-400">{image.camera}</span>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="p-1.5 rounded-md text-surface-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Download image"
            aria-label="Download image"
          >
            {downloading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

interface LightboxProps {
  images: ScanImage[];
  index: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
  onDownload: (image: ScanImage) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: Transform = { scale: 1, tx: 0, ty: 0 };

function Lightbox({ images, index, onClose, onIndexChange, onDownload }: LightboxProps) {
  const image = images[index];
  const total = images.length;

  const [errored, setErrored] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [transform, setTransform] = useState<Transform>(IDENTITY);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseTx: number;
    baseTy: number;
    pointerId: number;
  } | null>(null);

  // Reset zoom/pan and error state whenever the displayed image changes.
  useEffect(() => {
    setErrored(false);
    setTransform(IDENTITY);
  }, [image?.url]);

  // Keyboard navigation (left/right arrows). Esc is handled by the parent.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onIndexChange((index - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        onIndexChange((index + 1) % total);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onIndexChange]);

  // Wheel-zoom with cursor anchoring. Attached as a non-passive listener so
  // we can preventDefault and stop the page/modal from scrolling.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setTransform((prev) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        if (nextScale === MIN_SCALE) return IDENTITY;
        const ratio = nextScale / prev.scale;
        return {
          scale: nextScale,
          tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
          ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownload(image);
    } finally {
      setDownloading(false);
    }
  };

  const goPrev = () => onIndexChange((index - 1 + total) % total);
  const goNext = () => onIndexChange((index + 1) % total);

  const onPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (transform.scale <= MIN_SCALE) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: transform.tx,
      baseTy: transform.ty,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    e.preventDefault();
    setTransform((prev) => ({
      ...prev,
      tx: d.baseTx + (e.clientX - d.startX),
      ty: d.baseTy + (e.clientY - d.startY),
    }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (d && e.pointerId === d.pointerId) {
      dragRef.current = null;
    }
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setTransform((prev) => {
      if (prev.scale > MIN_SCALE) return IDENTITY;
      const nextScale = 2;
      const ratio = nextScale / prev.scale;
      return {
        scale: nextScale,
        tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
        ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
      };
    });
  };

  const resetZoom = () => setTransform(IDENTITY);

  const isZoomed = transform.scale > MIN_SCALE;
  const cursorClass = isZoomed
    ? dragRef.current
      ? 'cursor-grabbing'
      : 'cursor-grab'
    : 'cursor-zoom-in';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center select-none">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />

      {/* Top-right controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
        {isZoomed && (
          <button
            type="button"
            onClick={resetZoom}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium backdrop-blur transition-colors"
            title="Reset zoom"
            aria-label="Reset zoom"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9l6 6m0-6l-6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{Math.round(transform.scale * 100)}%</span>
          </button>
        )}
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium backdrop-blur disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Download image"
          aria-label="Download image"
        >
          {downloading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
          )}
          <span>{downloading ? 'Saving...' : 'Download'}</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors"
          aria-label="Close preview"
          title="Close (Esc)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Counter */}
      {total > 1 && (
        <div className="absolute top-4 left-4 px-3 py-2 rounded-lg bg-white/10 text-white text-sm font-medium backdrop-blur z-20">
          {index + 1} / {total}
        </div>
      )}

      {/* Prev / Next buttons */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors z-20"
            aria-label="Previous image"
            title="Previous (←)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors z-20"
            aria-label="Next image"
            title="Next (→)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Image container (handles wheel-zoom and dbl-click). */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={onDoubleClick}
      >
        {errored ? (
          <div className="text-white/80 text-sm flex flex-col items-center gap-2 p-8">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Failed to load image</span>
          </div>
        ) : (
          <img
            key={image.url}
            src={image.url}
            alt={image.label}
            draggable={false}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onError={() => setErrored(true)}
            className={`max-w-[92vw] max-h-[85vh] object-contain rounded-lg shadow-2xl ${cursorClass}`}
            style={{
              transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
              transformOrigin: 'center center',
              transition: dragRef.current ? 'none' : 'transform 80ms ease-out',
              willChange: 'transform',
            }}
          />
        )}
      </div>

      {/* Caption */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg bg-white/10 text-white text-sm backdrop-blur flex items-center gap-3 z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-medium capitalize">{image.label.replace(/_/g, ' ')}</span>
        {image.camera && (
          <span className="font-mono text-xs text-white/70">{image.camera}</span>
        )}
      </div>
    </div>
  );
}

export function ImagesModal({ isOpen, jobId, scanId, onClose }: ImagesModalProps) {
  const [data, setData] = useState<ScanImagesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);

  const allImages = useMemo<ScanImage[]>(() => {
    if (!data) return [];
    return [...data.angles, ...data.corners];
  }, [data]);

  useEffect(() => {
    if (!isOpen || !jobId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    fetchScanImages(jobId)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch images for job ${jobId}:`, err);
          setError(err instanceof Error ? err.message : 'Failed to load images');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, reloadKey]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (previewIndex !== null) {
        setPreviewIndex(null);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, previewIndex]);

  useEffect(() => {
    if (!isOpen) {
      setPreviewIndex(null);
      setBulkProgress(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDownload = (image: ScanImage) => downloadImage(image, scanId);

  const openPreviewByImage = (image: ScanImage) => {
    const idx = allImages.findIndex((it) => it.url === image.url);
    if (idx >= 0) setPreviewIndex(idx);
  };

  const handleDownloadAll = async () => {
    if (bulkProgress || allImages.length === 0) return;
    setBulkProgress({ current: 0, total: allImages.length });
    for (let i = 0; i < allImages.length; i++) {
      setBulkProgress({ current: i + 1, total: allImages.length });
      await downloadImage(allImages[i], scanId);
    }
    setBulkProgress(null);
  };

  const canDownloadAll = !!data && !isLoading && !error && allImages.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-6xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-200 flex items-center justify-between bg-surface-50">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-surface-800">Generated Images</h3>
              {scanId && (
                <p className="text-xs font-mono text-surface-500">{scanId}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canDownloadAll && (
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={!!bulkProgress}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title="Download all images"
                aria-label="Download all images"
              >
                {bulkProgress ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                )}
                <span>
                  {bulkProgress
                    ? `Downloading ${bulkProgress.current}/${bulkProgress.total}...`
                    : `Download all (${allImages.length})`}
                </span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading && (
            <div className="p-12 text-center">
              <div className="inline-flex items-center gap-3 text-surface-500">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading images...
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="p-12 text-center">
              <div className="inline-flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-danger-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-surface-700 font-medium">Failed to load images</p>
                  <p className="text-sm text-surface-500">{error}</p>
                </div>
                <button
                  onClick={() => setReloadKey((k) => k + 1)}
                  className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {data && !isLoading && !error && (
            <div className="space-y-8">
              <section>
                <h4 className="text-sm font-semibold text-surface-700 uppercase tracking-wider mb-3">
                  360 Angles
                </h4>
                {data.angles.length === 0 ? (
                  <p className="text-sm text-surface-400 italic">No angle images available.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.angles.map((img) => (
                      <ImageCard
                        key={`angle-${img.label}`}
                        image={img}
                        onOpen={openPreviewByImage}
                        onDownload={handleDownload}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h4 className="text-sm font-semibold text-surface-700 uppercase tracking-wider mb-3">
                  Corners
                </h4>
                {data.corners.length === 0 ? (
                  <p className="text-sm text-surface-400 italic">No corner images available.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.corners.map((img) => (
                      <ImageCard
                        key={`corner-${img.camera ?? img.label}`}
                        image={img}
                        onOpen={openPreviewByImage}
                        onDownload={handleDownload}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        {data && !isLoading && !error && (
          <div className="px-6 py-3 border-t border-surface-200 bg-surface-50 text-xs text-surface-500">
            Image links expire in {Math.round(data.expires_in / 60)} minutes. Click an image to enlarge (use ←/→ to navigate, scroll to zoom, drag to pan), or use the download buttons to save.
          </div>
        )}
      </div>

      {previewIndex !== null && allImages[previewIndex] && (
        <Lightbox
          images={allImages}
          index={previewIndex}
          onClose={() => setPreviewIndex(null)}
          onIndexChange={setPreviewIndex}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}
